import React, { useState, useEffect, useCallback } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft, Bell, Check, Minus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "./LoadingSpinner";

export default function Reminders() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [activeTab, setActiveTab] = useState("all");
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReminders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/children/reminders/by-child/?child_id=${childId}`);
      setReminders(response.data || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    fetchReminders();
  }, [childId, fetchReminders]);

  const handleMarkComplete = async (reminderId) => {
    try {
      await api.post(`/children/reminders/${reminderId}/mark-completed/`);
      setReminders(reminders.map(r => 
        r.id === reminderId ? { ...r, status: 'completed' } : r
      ));
    } catch (error) {
      console.error("Error marking reminder as complete:", error);
      alert("Failed to mark reminder as complete");
    }
  };

  const handleSnooze = async (reminderId) => {
    try {
      const snoozeUntil = new Date();
      snoozeUntil.setHours(snoozeUntil.getHours() + 1);
      
      await api.post(`/children/reminders/${reminderId}/snooze/`, {
        snooze_until: snoozeUntil.toISOString()
      });
      
      setReminders(reminders.map(r => 
        r.id === reminderId ? { ...r, status: 'snoozed' } : r
      ));
    } catch (error) {
      console.error("Error snoozing reminder:", error);
      alert("Failed to snooze reminder");
    }
  };

  const getFilteredReminders = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (activeTab) {
      case "all":
        return reminders.filter(r => r.status === 'pending');
      case "done":
        return reminders.filter(r => r.status === 'completed');
      case "snooze":
        return reminders.filter(r => r.status === 'snoozed');
      case "passed":
        return reminders.filter(r => {
          const reminderDate = new Date(r.reminder_date);
          return reminderDate < today && r.status === 'pending';
        });
      default:
        return reminders;
    }
  };

  const tabs = [
    { id: "all", label: "All Reminders" },
    { id: "done", label: "Done" },
    { id: "snooze", label: "Snooze" },
    { id: "passed", label: "Passed" }
  ];

  const getReminderIcon = (reminder) => {
    if (activeTab === "done") {
      return (
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
          <Check className="w-6 h-6 md:w-7 md:h-7 text-white stroke-[3]" />
        </div>
      );
    }
    if (activeTab === "snooze") {
      return (
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0">
          <Minus className="w-6 h-6 md:w-7 md:h-7 text-white stroke-[3]" />
        </div>
      );
    }
    return (
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-orange-400 flex items-center justify-center flex-shrink-0">
        <Bell className="w-6 h-6 md:w-7 md:h-7 text-white fill-current" />
      </div>
    );
  };

  const getCardBackground = () => {
    if (activeTab === "done") return "bg-green-50";
    if (activeTab === "snooze") return "bg-yellow-50";
    if (activeTab === "passed") return "bg-red-50";
    return "bg-blue-50";
  };

  const filteredReminders = getFilteredReminders();

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />
      <div className="relative z-10 min-h-screen pb-8">
        {/* Header */}
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">
            Reminders
          </h1>
          <div className="w-10" />
        </div>

        {/* Tabs */}
        <div className="px-5 md:px-8 lg:px-12 pt-6">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-2.5 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? "bg-[#1e3a5f] text-white shadow-md"
                    : "bg-white/70 text-[#1e3a5f] hover:bg-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-5 md:px-8 lg:px-12 pt-6 pb-8">
          <div className="max-w-5xl mx-auto">
            {filteredReminders.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredReminders.map((reminder) => (
                  <div
                    key={reminder.id}
                    className={`${getCardBackground()} rounded-2xl p-5 flex gap-4 items-start shadow-sm`}
                  >
                    {/* Icon */}
                    {getReminderIcon(reminder)}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-bold text-[#1e3a5f] mb-2 leading-tight">
                        {reminder.title}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                        Scheduled for {new Date(reminder.reminder_date).toLocaleDateString('en-US', { 
                          month: 'long', 
                          day: 'numeric' 
                        })} at {reminder.formatted_time}. Don't forget to prepare any questions you might have!
                      </p>
                    </div>

                    {/* Action Buttons - Only show for "All Reminders" tab */}
                    {activeTab === "all" && (
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleMarkComplete(reminder.id)}
                          className="w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors"
                        >
                          <Check className="w-5 h-5 text-white stroke-[3]" />
                        </button>
                        <button
                          onClick={() => handleSnooze(reminder.id)}
                          className="w-9 h-9 rounded-full bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center transition-colors"
                        >
                          <Minus className="w-5 h-5 text-white stroke-[3]" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-base">
                  {activeTab === "all" && "No pending reminders"}
                  {activeTab === "done" && "No completed reminders"}
                  {activeTab === "snooze" && "No snoozed reminders"}
                  {activeTab === "passed" && "No passed reminders"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
